import moment from "moment";

import DashboardService from "@/services/dashboardService";
import IHistoricReservation from '@/interfaces/IHistoricReservation';
import IDayPercentage from "../interfaces/IDayPercentage";
import IDepartmentPercentage from "../interfaces/IDepartmentPercentage";
import IFloorPercentage from "../interfaces/IFloorPercentage";
import { FilterOptions } from "../interfaces/filterOptions";
import IPlacePercentage from "../interfaces/IPlacePercentage";

import IMonthPercentage from "../interfaces/IMonthPercentage";
import IWeekPercentage from "../interfaces/IWeekPercentage";
import VenueProvider from "@/providers/venueProvider";
import ReservationInsightsFilter, { mapToPlaceFilterRequest } from "../interfaces/reservationInsightsFilter";
import { ReservedTimeResponse } from "../interfaces/responses/reservedTimeResponse";
import { IWeekDayHourPercentage } from "../interfaces/IWeekDayHourPercentage";
import IPlaceTypePercentage from "../interfaces/IPlaceTypePercentage";

export default class HistoricReservationService {
    private readonly dashboardService: DashboardService;

    public constructor() {
        this.dashboardService = new DashboardService();
    }

    public async getNoShowHistoricReservationsForVenue(startDate: Date, endDate: Date, onlyNoShows: boolean = false): Promise<IHistoricReservation[]> {
        const venue = VenueProvider.getActiveVenue();
        if (venue === undefined) {
            throw new Error("Error getting venue settings: No venue found in venueprovider!");
        }

        return this.dashboardService.get<IHistoricReservation[]>(`reservations/customer/${venue.customerId}/venue/${venue.id}/from/${this.formatDate(startDate)}/to/${this.formatDate(endDate)}${onlyNoShows ? 'isNoShow=true' : ''}`);
    }

    public async getNoShowHistoricReservationsForFloor(floorId: string, startDate: Date, endDate: Date, onlyNoShows: boolean = false): Promise<IHistoricReservation[]> {
        const venue = VenueProvider.getActiveVenue();

        if (venue === undefined) {
            throw new Error("Error getting venue settings: No venue found in venueprovider!");
        }

        return this.dashboardService.get<IHistoricReservation[]>(`reservations/customer/${venue.customerId}/floor/${floorId}/from/${this.formatDate(startDate)}/to/${this.formatDate(endDate)}${onlyNoShows ? 'isNoShow=true' : ''}`);
    }

    public async getPlaceFilterOptions(): Promise<FilterOptions> {
        return this.dashboardService.get<FilterOptions>(`reporting/placefilteroptions`);
    }

    public async getPlaceReservationsReservedtime(filter: ReservationInsightsFilter): Promise<ReservedTimeResponse> {
        const mappedFilter = mapToPlaceFilterRequest(filter);
        return this.dashboardService.postWithResponse<ReservedTimeResponse>(`reporting/placereservations/reservedtime`, mappedFilter);
    }

    public async getPlaceReservationsWeekDayHourPercentage(filter: ReservationInsightsFilter): Promise<IWeekDayHourPercentage[]> {
        const mappedFilter = mapToPlaceFilterRequest(filter);
        return this.dashboardService.postWithResponse<IWeekDayHourPercentage[]>(`reporting/placereservations/reservedweekdayhourpercentages`, mappedFilter);
    }

    public async getPlaceReservationsWeekPercentage(filter: ReservationInsightsFilter): Promise<IWeekPercentage[]> {
        const mappedFilter = mapToPlaceFilterRequest(filter);
        return this.dashboardService.postWithResponse<IWeekPercentage[]>(`reporting/placereservations/reservedweekpercentages`, mappedFilter);
    }

    public async getPlaceReservationsMonthPercentage(filter: ReservationInsightsFilter): Promise<IMonthPercentage[]> {
        const mappedFilter = mapToPlaceFilterRequest(filter);
        return this.dashboardService.postWithResponse<IMonthPercentage[]>(`reporting/placereservations/reservedmonthpercentages`, mappedFilter);
    }

    public async getPlacePlaceTypePercentage(filter: ReservationInsightsFilter): Promise<IPlaceTypePercentage[]> {
        const mappedFilter = mapToPlaceFilterRequest(filter);
        return this.dashboardService.postWithResponse<IPlaceTypePercentage[]>(`reporting/placereservations/reservedplacetypepercentages`, mappedFilter);
    }

    public async getPlaceReservationsDayPercentage(filter: ReservationInsightsFilter): Promise<IDayPercentage[]> {
        const mappedFilter = mapToPlaceFilterRequest(filter);
        const response = await this.dashboardService.postWithResponse<IDayPercentage[]>(`reporting/placereservations/reserveddaypercentages`, mappedFilter);

        const convertedResponse = response.map(item => ({
            ...item,
            day: new Date(item.day)
        }));

        return convertedResponse;
    }

    public async getPlaceReservationsFloorPercentage(filter: ReservationInsightsFilter): Promise<IFloorPercentage[]> {
        const mappedFilter = mapToPlaceFilterRequest(filter);
        return this.dashboardService.postWithResponse<IFloorPercentage[]>(`reporting/placereservations/reservedfloorpercentages`, mappedFilter);
    }

    public async getPlaceReservationsDepartmentPercentage(filter: ReservationInsightsFilter): Promise<IDepartmentPercentage[]> {
        const mappedFilter = mapToPlaceFilterRequest(filter);
        return this.dashboardService.postWithResponse<IDepartmentPercentage[]>(`reporting/placereservations/reserveddepartmentpercentages`, mappedFilter);
    }

    public async getPlaceReservationsPlacePercentage(filter: ReservationInsightsFilter): Promise<IPlacePercentage[]> {
        const mappedFilter = mapToPlaceFilterRequest(filter);
        return this.dashboardService.postWithResponse<IPlacePercentage[]>(`reporting/placereservations/reservedplacepercentages`, mappedFilter);
    }

    private formatDate(date: Date): string {
        return moment(date).format("YYYY-MM-DD");
    }
}